import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Stack, Typography } from '@mui/material';

import dayjs from 'dayjs';

import PriceText from '@/shared/ui/PriceText';

const FAILED_STATUSES = ['CANCELLED', 'REJECTED'];
const DATE_FORMAT = 'DD.MM.YYYY';

interface Props {
  attributes: Record<string, string>;
}

const SearchOrderInsights: FC<Props> = ({ attributes }) => {
  const { t } = useTranslation();
  const {
    status,
    totalCost,
    budget,
    monthlyPrice,
    weeklyPrice,
    startDate,
    endDate,
    advSpaces,
    advPackages,
  } = attributes;
  const money = totalCost ?? budget ?? monthlyPrice;
  const moneyLabelKey = totalCost
    ? 'search.preview.total_cost'
    : budget
      ? 'search.preview.budget'
      : 'search.preview.monthly_price';
  const hasBookingCounts = advSpaces !== undefined || advPackages !== undefined;
  const statCount = (money ? 1 : 0) + (weeklyPrice ? 1 : 0) + (hasBookingCounts ? 2 : 0);
  const statColumns = statCount === 3 ? '1.4fr 1fr 1fr' : `repeat(${Math.max(statCount, 1)}, 1fr)`;

  const hasStats = Boolean(money || weeklyPrice || hasBookingCounts);
  const isFailed = Boolean(status && FAILED_STATUSES.includes(status));

  const start = startDate ? dayjs(startDate) : null;
  const end = endDate ? dayjs(endDate) : null;
  const hasPeriod = Boolean(start?.isValid() && end?.isValid());
  const totalDays = hasPeriod ? Math.max(1, end!.diff(start!, 'day')) : 0;
  const elapsedDays = hasPeriod ? dayjs().diff(start!, 'day') : 0;
  const progress = hasPeriod ? Math.max(0, Math.min(100, (elapsedDays / totalDays) * 100)) : 0;

  const periodLabel = !hasPeriod
    ? ''
    : isFailed
      ? t('search.preview.not_running')
      : elapsedDays < 0
        ? t('search.preview.starts_in', { days: Math.abs(elapsedDays) })
        : elapsedDays > totalDays
          ? t('search.preview.ended')
          : t('search.preview.day_of', { day: Math.max(1, elapsedDays + 1), total: totalDays });

  if (!hasStats && !hasPeriod) return null;

  return (
    <Stack gap="0.75rem" data-testid="search-order-insights">
      {hasStats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: statColumns, gap: '0.5rem' }}>
          {money && (
            <InsightCard>
              <StatLabel>{t(moneyLabelKey)}</StatLabel>
              <StatValue component="div">
                <PriceText price={parseFloat(money)} />
              </StatValue>
            </InsightCard>
          )}
          {weeklyPrice && (
            <InsightCard>
              <StatLabel>{t('search.preview.weekly_price')}</StatLabel>
              <StatValue component="div">
                <PriceText price={parseFloat(weeklyPrice)} />
              </StatValue>
            </InsightCard>
          )}
          {hasBookingCounts && (
            <InsightCard>
              <StatLabel>{t('search.preview.adv_spaces')}</StatLabel>
              <StatValue>{advSpaces ?? 0}</StatValue>
            </InsightCard>
          )}
          {hasBookingCounts && (
            <InsightCard>
              <StatLabel>{t('search.preview.adv_packages')}</StatLabel>
              <StatValue>{advPackages ?? 0}</StatValue>
            </InsightCard>
          )}
        </Box>
      )}

      {hasPeriod && (
        <InsightCard>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap="0.625rem">
            <Typography
              sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.900' }}
            >
              {`${start!.format(DATE_FORMAT)} – ${end!.format(DATE_FORMAT)}`}
            </Typography>
            <Typography
              sx={{ fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.900' }}
            >
              {periodLabel}
            </Typography>
          </Stack>
          <Box
            sx={{
              height: '0.4375rem',
              borderRadius: '1.5rem',
              backgroundColor: 'grey.100',
              overflow: 'hidden',
              marginTop: '0.5rem',
              opacity: isFailed ? 0.5 : 1,
            }}
          >
            <Box
              sx={{
                width: `${isFailed ? 0 : progress}%`,
                height: '100%',
                borderRadius: '1.5rem',
                backgroundColor: 'accent.main',
              }}
            />
          </Box>
        </InsightCard>
      )}
    </Stack>
  );
};

const StatLabel: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    display="block"
    sx={{
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.25,
      color: 'grey.900',
      whiteSpace: 'pre-line',
    }}
  >
    {children}
  </Typography>
);

const StatValue: FC<{ children: React.ReactNode; component?: 'div' }> = ({
  children,
  component = 'p',
}) => (
  <Typography
    component={component}
    sx={{
      fontSize: '1.125rem',
      fontWeight: 400,
      lineHeight: 1,
      color: 'grey.900',
      whiteSpace: 'nowrap',
      marginTop: 'auto',
      paddingTop: '0.375rem',
    }}
  >
    {children}
  </Typography>
);

const InsightCard: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      padding: '0.625rem 0.75rem',
      borderRadius: '0.75rem',
      backgroundColor: 'background.paper',
    }}
  >
    {children}
  </Box>
);

export default SearchOrderInsights;
